import React from 'react';
import PropTypes from 'prop-types';
import TextField from './TextField';
import TextAreaField from './TextAreaField';
import NumberField from './NumberField';
import DateField from './DateField';
import EmailField from './EmailField';
import PhoneField from './PhoneField';
import CheckboxField from './CheckboxField';
import SelectField from './SelectField';
import FileUploadField from './FileUploadField';
import EmployeeSearchField from './EmployeeSearchField';
import WorkflowFileField from '../../ScreenLayouts/CustomerType/Workflow/WorkflowFileField';
import MultiCertificateField from './MultiCertificateField';

/**
 * FieldFactory component that renders the appropriate field component
 * based on the field type.
 */
const FieldFactory = ({
  field,
  value,
  onChange,
  onBlur,
  error,
  helperText,
  disabled,
}) => {
  const commonProps = {
    id: field.id,
    name: field.name,
    label: field.label,
    required: field.required,
    disabled: disabled || field.disabled,
    value: value,
    onChange: onChange,
    onBlur: onBlur,
    error: error,
    helperText: helperText || field.helperText,
  };

  // Multiple-instance certificate fields (educational / experience certificates) render a popup that
  // manages several titled certificates, each with multiple files.
  const isMultiCertField = (f) => {
    const s = `${f.name || ''} ${f.code || ''} ${f.title || ''}`.toLowerCase();
    if (s.includes('number') || s.includes('date')) return false; // e.g. "certificate number" is a text field
    return s.includes('educationalcertificate') || s.includes('experiencecertificate') ||
      (s.includes('education') && s.includes('cert')) || (s.includes('experience') && s.includes('cert'));
  };
  if (isMultiCertField(field)) {
    return (
      <MultiCertificateField
        field={field}
        value={value}
        onChange={onChange}
        disabled={disabled || field.disabled}
      />
    );
  }

  // Check if this is a document field from employee data
  const isDocumentField = (fieldName, fieldType) => {
    // Prefer explicit type first
    const lowerFieldName = (fieldName || '').toLowerCase();
    const lowerFieldType = (fieldType || '').toLowerCase();

    if (lowerFieldType === 'file' || lowerFieldType === 'document') {
      return true;
    }

    // Known document fields by name (allowlist)
    const knownDocFields = new Set([
      'passportcopy',
      'visapage',
      'birthcertificate',
      'salarycertificate',
      'labourcontract',
      'emiratesid',
      'photo',
      'shinasnama'
    ]);

    // Special case: ensure emirates_id_number (and similar) are NOT treated as documents
    if (lowerFieldName.endsWith('_number') || lowerFieldName.includes('number')) {
      return false;
    }

    // Only treat as document if in allowlist
    return knownDocFields.has(lowerFieldName);
  };
  
  // Special case for document fields that might be auto-populated from employee data
  if (isDocumentField(field.name, field.type)) {
    console.log('RENDERING DOCUMENT FIELD WITH WORKFLOW FILE FIELD:', field);
    
    // Extract form data and field name for the WorkflowFileField
    const formData = window.currentFormData || {};
    const fieldName = field.name;
    
    // Check if we have a document reference in the value or form data
    const hasDocumentReference = 
      (typeof value === 'object' && value !== null && (value.isReference || value.hasFile)) ||
      formData?.additionalInfo?.[fieldName]?.isReference ||
      formData?.additionalInfo?.[fieldName]?.hasFile;
    
    if (hasDocumentReference) {
      console.log(`Field ${fieldName} has a document reference, using WorkflowFileField`);
      return (
        <WorkflowFileField
          field={field}
          fieldName={fieldName}
          formData={formData}
          setFormData={(updatedFormData) => {
            // Update the global form data reference
            window.currentFormData = updatedFormData;
            // Call the onChange handler with the updated value
            onChange({
              target: {
                name: fieldName,
                value: updatedFormData.additionalInfo[fieldName]
              }
            });
          }}
        />
      );
    }
  }
  
  // Special case for birthCertificate field
  if (field.name.toLowerCase().includes('birth') && field.name.toLowerCase().includes('certificate')) {
    console.log('RENDERING BIRTH CERTIFICATE AS FILE UPLOAD:', field);
    return (
      <FileUploadField
        {...commonProps}
        accept="image/*,.pdf"
        maxSize={5 * 1024 * 1024} // 5MB
      />
    );
  }
  
  // EARLY OVERRIDES: contact and gender fields must render as proper inputs even if mis-typed
  const earlyLName = (field.name || '').toLowerCase();
  const earlyLCode = (field.code || '').toLowerCase();
  const earlyLTitle = (field.title || '').toLowerCase();
  const earlyType = (field.type || '').toString().toUpperCase();
  const earlyLooksLikeGender = earlyLName.includes('gender') || earlyLCode.includes('gender') || earlyLTitle.includes('gender');
  if (earlyType === 'EMAIL' || earlyLName === 'email' || earlyLName.includes('email') || earlyLCode.includes('email') || earlyLTitle.includes('email')) {
    return (
      <EmailField
        {...commonProps}
        placeholder={field.placeholder || 'example@domain.com'}
      />
    );
  }
  if (earlyType === 'PHONE' || earlyType === 'MOBILE' || earlyLName.includes('phone') || earlyLName.includes('mobile') || earlyLCode.includes('phone') || earlyLTitle.includes('phone')) {
    return (
      <PhoneField
        {...commonProps}
        placeholder={field.placeholder || '+XX XXXX XXXXXX'}
      />
    );
  }
  if (earlyLooksLikeGender) {
    const baseOptions = Array.isArray(field.options) && field.options.length > 0
      ? field.options
      : [
          { value: 'MALE', label: 'Male' },
          { value: 'FEMALE', label: 'Female' }
        ];
    let currentValue = value;
    try {
      if (typeof currentValue === 'string') {
        const rawU = currentValue.toUpperCase();
        const vals = baseOptions.map(o => (typeof o === 'object' ? o.value : o));
        const labels = baseOptions.map(o => (typeof o === 'object' ? o.label : o));
        if (vals.includes(currentValue) || vals.includes(rawU)) {
          currentValue = vals.includes(currentValue) ? currentValue : rawU;
        } else {
          const idx = labels.findIndex(l => String(l).toLowerCase() === currentValue.toLowerCase());
          if (idx >= 0) currentValue = vals[idx] || labels[idx];
        }
      }
    } catch (e) {}
    return (
      <SelectField
        {...commonProps}
        value={currentValue}
        options={baseOptions}
      />
    );
  }

  // Check if value is a document object that needs special handling
  // IMPORTANT: Only apply this path for fields that are recognized as document fields.
  if (isDocumentField(field.name, field.type)) {
    if (typeof value === 'object' && value !== null && !Array.isArray(value) &&
        (value.isReference || value.hasFile || value.target || value.documentUrl || value.path || value.id)) {
      // Handle nested target structure
      let documentValue = value;
      if (value.target && typeof value.target === 'object' && value.target.value) {
        documentValue = value.target.value;
      }
      // Quiet noisy logs
      // console.log('RENDERING OBJECT DOCUMENT FIELD WITH WORKFLOW FILE FIELD:', field.name, documentValue);
      return (
        <WorkflowFileField
          field={field}
          fieldName={field.name}
          formData={{
            additionalInfo: {
              [field.name]: documentValue
            }
          }}
          setFormData={(updatedFormData) => {
            // Call the onChange handler with the updated value
            let updatedValue = updatedFormData.additionalInfo[field.name];
            
            // If the original value had a target structure, preserve it
            if (value.target && typeof value.target === 'object') {
              updatedValue = {
                ...value,
                target: {
                  ...value.target,
                  value: updatedValue
                }
              };
            }
            
            onChange({
              target: {
                name: field.name,
                value: updatedValue
              }
            });
          }}
        />
      );
    }
  }

  // Guard: if the field is a known contact field, render appropriate input even if mis-typed
  const lname = (field.name || '').toLowerCase();
  const lcode = (field.code || '').toLowerCase();
  const ltitle = (field.title || '').toLowerCase();
  const looksLikeGender = lname.includes('gender') || lcode.includes('gender') || ltitle.includes('gender');
  if (lname === 'email' || lname.includes('email')) {
    return (
      <EmailField
        {...commonProps}
        placeholder={field.placeholder || 'example@domain.com'}
      />
    );
  }
  if (lname.includes('phone') || lname.includes('mobile')) {
    return (
      <PhoneField
        {...commonProps}
        placeholder={field.placeholder || '+XX XXXX XXXXXX'}
      />
    );
  }

  // Guard: render gender as a select with standard options
  if (looksLikeGender) {
    // Standardize options; if backend provided options, use them, else default
    const baseOptions = Array.isArray(field.options) && field.options.length > 0
      ? field.options
      : [
          { value: 'MALE', label: 'Male' },
          { value: 'FEMALE', label: 'Female' }
        ];

    // Normalize current value for display without mutating form state
    let currentValue = value;
    try {
      if (typeof currentValue === 'string') {
        const rawU = currentValue.toUpperCase();
        const vals = baseOptions.map(o => (typeof o === 'object' ? o.value : o));
        const labels = baseOptions.map(o => (typeof o === 'object' ? o.label : o));
        if (vals.includes(currentValue) || vals.includes(rawU)) {
          currentValue = vals.includes(currentValue) ? currentValue : rawU;
        } else {
          const idx = labels.findIndex(l => String(l).toLowerCase() === currentValue.toLowerCase());
          if (idx >= 0) currentValue = vals[idx] || labels[idx];
        }
      }
    } catch (e) {
      // ignore normalization errors
    }

    return (
      <SelectField
        {...commonProps}
        value={currentValue}
        options={baseOptions}
      />
    );
  }

  // Handle file type fields
  if (field.type === 'file') {
    console.log('RENDERING FILE UPLOAD FIELD:', field);
    return (
      <FileUploadField
        {...commonProps}
        accept={field.accept || "*"}
        maxSize={field.maxSize || 5 * 1024 * 1024} // Default 5MB
      />
    );
  }
  
  switch (field.type) {
    case 'text':
      return (
        <TextField
          {...commonProps}
          placeholder={field.placeholder}
          maxLength={field.maxLength}
        />
      );
    case 'textarea':
      return (
        <TextAreaField
          {...commonProps}
          placeholder={field.placeholder}
          rows={field.rows || 4}
          maxLength={field.maxLength}
        />
      );
    case 'number':
      return (
        <NumberField
          {...commonProps}
          placeholder={field.placeholder}
          min={field.min}
          max={field.max}
          step={field.step || 1}
        />
      );
    case 'date':
      return (
        <DateField
          {...commonProps}
          minDate={field.minDate ? new Date(field.minDate) : undefined}
          maxDate={field.maxDate ? new Date(field.maxDate) : undefined}
          disablePast={field.disablePast}
          disableFuture={field.disableFuture}
        />
      );
    case 'email':
      return (
        <EmailField
          {...commonProps}
          placeholder={field.placeholder || 'example@domain.com'}
        />
      );
    case 'phone':
      return (
        <PhoneField
          {...commonProps}
          placeholder={field.placeholder || '+XX XXXX XXXXXX'}
        />
      );
    case 'checkbox':
      return <CheckboxField {...commonProps} />;
    case 'select':
      return (
        <SelectField
          {...commonProps}
          options={field.options || []}
          placeholder={field.placeholder || 'Select an option'}
          multiple={field.multiple}
        />
      );
    case 'file':
      return (
        <FileUploadField
          {...commonProps}
          accept={field.accept}
          maxSize={field.maxSize}
        />
      );
    case 'employee':
      return (
        <EmployeeSearchField
          {...commonProps}
          placeholder={field.placeholder || 'Search for an employee'}
        />
      );
    default:
      return (
        <TextField
          {...commonProps}
          placeholder={field.placeholder}
        />
      );
  }
};

FieldFactory.propTypes = {
  field: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
    label: PropTypes.string,
    type: PropTypes.oneOf([
      'text',
      'employee',
      'textarea',
      'number',
      'date',
      'email',
      'phone',
      'checkbox',
      'select',
      'file',
    ]).isRequired,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    placeholder: PropTypes.string,
    helperText: PropTypes.string,
    // Field-specific props
    options: PropTypes.array, // for select
    multiple: PropTypes.bool, // for select
    rows: PropTypes.number, // for textarea
    maxLength: PropTypes.number, // for text/textarea
    min: PropTypes.number, // for number
    max: PropTypes.number, // for number
    step: PropTypes.number, // for number
    minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]), // for date
    maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]), // for date
    disablePast: PropTypes.bool, // for date
    disableFuture: PropTypes.bool, // for date
    accept: PropTypes.string, // for file upload (e.g., 'image/*', '.pdf')
    maxSize: PropTypes.number, // for file upload (max file size in bytes)
  }).isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
};

export default FieldFactory;
